/**
 * Nova entrada de estoque (manual) — replicada do sistema antigo.
 *
 * Para lançar entrada direto, sem passar pela análise de foto: fornecedor e
 * NF valem para o lançamento todo; cada item pode ter marca, lote e validade
 * próprios — o mesmo código recebe vários lotes diferentes ao longo do tempo.
 *
 * A diferença para o antigo é o jeito de achar o item: em vez de datalist de
 * texto, o seletor com foto e saldo (`busca-item`) — escolher já adiciona a
 * linha, sem botão "+ adicionar" antes.
 *
 * (O campo "ID único" do antigo ficou de fora: o controle unitário por peça
 * não existe no modelo novo — se voltar, volta com desenho próprio.)
 */

import { api, ErroApi } from '../api.js';
import { escapar, numero } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';
import { montarBuscaItem, type ItemEscolhido } from '../comum/busca-item.js';

interface Linha {
  item: ItemEscolhido;
  quantidade: string;
  marca: string;
  lote: string;
  validade: string;
}

export function montarNovaEntrada(raiz: HTMLElement): void {
  let linhas: Linha[] = [];

  raiz.innerHTML = `
    <section class="ped-painel">
      <h3>📥 Nova entrada de estoque (manual)</h3>
      <p class="dica">Para lançar entrada direto, sem passar pela análise de foto.
        Cada item pode ter marca, lote e validade próprios.</p>
      <div class="form-2col">
        <label class="rotulado"><span>Fornecedor</span><input id="ne-fornecedor" maxlength="200"></label>
        <label class="rotulado"><span>Número da NF (opcional)</span><input id="ne-nf" maxlength="60"></label>
      </div>

      <h3>Itens recebidos</h3>
      <div id="ne-linhas"></div>
      <div id="ne-busca"></div>

      <div class="conf-acoes">
        <button class="btn btn-grande" id="ne-lancar">✓ Lançar entrada</button>
      </div>
      <p class="aviso" id="ne-erro" hidden></p>
    </section>`;

  const caixaLinhas = raiz.querySelector<HTMLElement>('#ne-linhas')!;
  const erro = raiz.querySelector<HTMLElement>('#ne-erro')!;

  const busca = montarBuscaItem(raiz.querySelector<HTMLElement>('#ne-busca')!, (item) => {
    linhas.push({ item, quantidade: '', marca: '', lote: '', validade: '' });
    desenharLinhas();
    // O olho está no fluxo de adicionar: foco na quantidade recém-criada.
    caixaLinhas.querySelector<HTMLInputElement>('.ne-linha:last-child [data-campo="quantidade"]')?.focus();
  });

  function desenharLinhas(): void {
    if (linhas.length === 0) {
      caixaLinhas.innerHTML = `<p class="dica">Busque um item abaixo — escolher já adiciona a linha.</p>`;
      return;
    }
    caixaLinhas.innerHTML = linhas
      .map(
        (l, i) => `
      <div class="ne-linha" data-i="${i}">
        <span class="ped-cfoto">${
          l.item.miniatura
            ? `<img src="${l.item.miniatura}" alt="" ${l.item.foto ? `data-grande="${escapar(api.foto(l.item.foto) ?? '')}" data-fotos-de="${escapar(l.item.codigo)}"` : ''}>`
            : '<span class="sem">—</span>'
        }</span>
        <span class="ped-cnome">
          <b>${escapar(l.item.nome)}</b>
          <span class="sub-inline">${escapar(l.item.codigo)} · saldo atual ${numero(l.item.saldo)} ${escapar(l.item.unidade ?? '')}</span>
          ${
            // Item sem foto: é AGORA, com o material na mão e o nome na tela,
            // que buscar a imagem custa menos — pedido do Lucas (05/08).
            !l.item.miniatura && !l.item.foto
              ? `<button type="button" class="ne-buscafoto" data-buscafoto="${i}">🔍 Buscar foto no Google</button>`
              : ''
          }
        </span>
        <label class="rotulado ne-c"><span>Qtd.</span>
          <input type="number" min="0.001" step="any" data-campo="quantidade" value="${escapar(l.quantidade)}"></label>
        <label class="rotulado ne-c"><span>Marca</span>
          <input maxlength="120" data-campo="marca" value="${escapar(l.marca)}"></label>
        <label class="rotulado ne-c"><span>Lote</span>
          <input maxlength="60" data-campo="lote" value="${escapar(l.lote)}"></label>
        <label class="rotulado ne-c"><span>Validade</span>
          <input type="date" data-campo="validade" value="${escapar(l.validade)}"></label>
        <button class="ped-tirar" data-tirar="${i}" title="Tirar da entrada">✕</button>
      </div>`,
      )
      .join('');
  }

  // Delegação: as linhas redesenham, o ouvinte fica.
  caixaLinhas.addEventListener('input', (e) => {
    const campo = e.target as HTMLInputElement;
    const linha = campo.closest<HTMLElement>('[data-i]');
    const chave = campo.dataset['campo'] as keyof Omit<Linha, 'item'> | undefined;
    if (!linha || !chave) return;
    linhas[Number(linha.dataset['i'])]![chave] = campo.value;
  });
  caixaLinhas.addEventListener('click', (e) => {
    const tirar = (e.target as HTMLElement).closest<HTMLElement>('[data-tirar]');
    if (tirar) {
      linhas.splice(Number(tirar.dataset['tirar']), 1);
      desenharLinhas();
      return;
    }
    const buscar = (e.target as HTMLElement).closest<HTMLElement>('[data-buscafoto]');
    if (buscar) void buscarFoto(Number(buscar.dataset['buscafoto']));
    const usar = (e.target as HTMLElement).closest<HTMLElement>('[data-usarfoto]');
    if (usar) void usarFoto(usar);
  });

  /**
   * A busca de imagem para item sem foto — as sugestões do Google aparecem
   * DENTRO da linha, e escolher uma importa para o nosso bucket e anexa ao
   * item na hora (a foto nunca fica como link para site alheio).
   */
  async function buscarFoto(i: number): Promise<void> {
    const l = linhas[i];
    const linha = caixaLinhas.querySelector(`[data-i="${i}"]`);
    if (!l || !linha) return;
    linha.querySelector('.ne-fotostrip')?.remove();
    const strip = document.createElement('div');
    strip.className = 'ne-fotostrip';
    strip.innerHTML = `<span class="dica">Buscando imagens de "${escapar(l.item.nome)}"…</span>`;
    linha.appendChild(strip);
    try {
      const r = await api.get<{ imagens: { imagem: string; miniatura: string; titulo: string }[] }>(
        `/api/itens/imagens?busca=${encodeURIComponent(l.item.nome)}`,
      );
      if (r.imagens.length === 0) {
        strip.innerHTML = `<span class="dica">Nenhuma imagem encontrada para esse nome.</span>`;
        return;
      }
      strip.innerHTML = `<span class="dica">Clique na imagem certa — ela vira a foto do item:</span>
        <div class="ne-sugestoes">${r.imagens
          .map(
            (s, j) => `<button type="button" class="ne-sug" data-usarfoto data-linha="${i}" data-img="${j}"
                        title="${escapar(s.titulo)}"><img src="${escapar(s.miniatura)}" alt=""
                        onerror="this.closest('button').remove()"></button>`,
          )
          .join('')}</div>`;
      sugestoesPorLinha.set(i, r.imagens);
    } catch (e) {
      strip.innerHTML = `<span class="dica">${escapar(
        e instanceof ErroApi ? e.message : 'A busca de imagens falhou agora.',
      )}</span>`;
    }
  }

  const sugestoesPorLinha = new Map<number, { imagem: string; miniatura: string }[]>();

  async function usarFoto(botao: HTMLElement): Promise<void> {
    const i = Number(botao.dataset['linha']);
    const j = Number(botao.dataset['img']);
    const l = linhas[i];
    const escolhida = sugestoesPorLinha.get(i)?.[j];
    if (!l || !escolhida) return;
    botao.classList.add('ne-sug-importando');
    try {
      const imp = await api.post<{ caminho: string }>('/api/fotos/importar', {
        url: escolhida.imagem,
        pasta: 'itens',
        referencia: l.item.codigo,
      });
      const anexada = await api.post<{ caminho: string; url: string; miniatura: string | null }>(
        `/api/itens/${encodeURIComponent(l.item.codigo)}/fotos`,
        { caminho: imp.caminho, capa: true },
      );
      l.item.miniatura = anexada.miniatura ?? escolhida.miniatura;
      l.item.foto = anexada.url;
      sugestoesPorLinha.delete(i);
      desenharLinhas();
      avisar(`Foto adicionada a ${l.item.codigo}.`);
    } catch (e) {
      avisar(e instanceof ErroApi ? e.message : 'Não consegui importar essa imagem.', 'atencao');
      botao.classList.remove('ne-sug-importando');
    }
  }

  raiz.querySelector('#ne-lancar')!.addEventListener('click', () => void lancar());

  async function lancar(): Promise<void> {
    erro.hidden = true;
    if (linhas.length === 0) {
      avisarBloqueante('Adicione ao menos um item — busque logo abaixo da lista.');
      return;
    }
    const semQtd = linhas.find((l) => !(Number(l.quantidade) > 0));
    if (semQtd) {
      avisarBloqueante(`"${semQtd.item.nome}" está sem quantidade.`);
      return;
    }

    const fornecedor = raiz.querySelector<HTMLInputElement>('#ne-fornecedor')!.value.trim();
    const nf = raiz.querySelector<HTMLInputElement>('#ne-nf')!.value.trim();
    const botao = raiz.querySelector<HTMLButtonElement>('#ne-lancar')!;
    botao.disabled = true;
    botao.textContent = 'Lançando…';

    // Um POST por item, EM ORDEM — e quem falhar fica na tela para tentar de
    // novo, em vez de um erro derrubar o lançamento inteiro (lição do v70 do
    // sistema antigo).
    const falhas: { linha: Linha; motivo: string }[] = [];
    let lancados = 0;
    for (const l of linhas) {
      try {
        await api.post('/api/movimentos/entrada', {
          codigo: l.item.codigo,
          quantidade: Number(l.quantidade),
          ...(fornecedor ? { fornecedor } : {}),
          ...(nf ? { numeroNf: nf } : {}),
          ...(l.marca.trim() ? { marca: l.marca.trim() } : {}),
          ...(l.lote.trim() ? { lote: l.lote.trim() } : {}),
          ...(l.validade ? { validade: l.validade } : {}),
        });
        lancados++;
      } catch (e) {
        falhas.push({
          linha: l,
          motivo: e instanceof ErroApi ? e.message : 'falha de rede',
        });
      }
    }

    botao.disabled = false;
    botao.textContent = '✓ Lançar entrada';

    if (falhas.length === 0) {
      linhas = [];
      desenharLinhas();
      raiz.querySelector<HTMLInputElement>('#ne-fornecedor')!.value = '';
      raiz.querySelector<HTMLInputElement>('#ne-nf')!.value = '';
      avisar(`${lancados} entrada(s) lançada(s) no estoque.`);
      busca.focar();
      return;
    }

    // Só o que falhou permanece — o que entrou, entrou.
    linhas = falhas.map((f) => f.linha);
    desenharLinhas();
    erro.innerHTML = `${lancados ? `${lancados} lançada(s). ` : ''}Não consegui lançar:<br>· ${falhas
      .map((f) => `${escapar(f.linha.item.codigo)} — ${escapar(f.motivo)}`)
      .join('<br>· ')}`;
    erro.hidden = false;
  }

  desenharLinhas();
}
